import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { MultiFacilitySeries } from "../../../utils/models";
import type { ClampRange } from "../utils";
import { MultiFacilityChartStateBox } from "./MultiFacilityChartStateBox";
import { dedupeFacilityDisplayNames, hasAnySeriesData, multiStatsToLineGroups, plainAxisTick, visibleYDomain } from "./utils";

/**
 * Multi-facility cards run taller than the single-mode chart defaults: the legend wraps (one entry
 * per facility, scrolling past LEGEND_MAX_HEIGHT) and the plot needs to keep its height beside it.
 * Shared with the disciplinary card so all multi-view chart bodies are the same height.
 */
export const MULTI_CHART_HEIGHTS = { xs: "240px", lg: "290px", xl: "340px", xxl: "390px" } as const;
const LEGEND_MAX_HEIGHT = 88;

type Props = {
	/** Already-translated card title. */
	title: string;
	dataTestId: string;
	/** One multi-facility metric (per-facility date→value series); undefined while loading. */
	series?: MultiFacilitySeries;
	/** dept_id → display name from the same response; undefined while loading. */
	facilityNames?: Record<string, string>;
	isLoading: boolean;
	isError: boolean;
	/** Sorted, stable facility order — drives deterministic colors and the legend order. */
	sortedDepartmentIds: string[];
	/**
	 * Page-selected subset of `sortedDepartmentIds` (order-preserving): only these facilities get a
	 * series BUILT — and a built series is VISIBLE (nothing is seeded hidden). Colors, name dedupe
	 * and the hidden-series seed key stay on the FULL set so reselecting restores identical
	 * suffixes/hues and keeps the per-chart ad-hoc legend toggles.
	 */
	visibleDepartmentIds: string[];
	/** Part of the hidden-series seed key: switching departments resets ad-hoc legend toggles. */
	currentDepartmentId: string;
	colors: Record<string, string>;
	clampRange?: ClampRange;
	/**
	 * When true the average anchor is the mean of the currently drawn facilities (selection minus
	 * legend-hidden); when false (default) it spans ALL facilities in the set, so the average stays
	 * consistent as the user narrows the selection.
	 */
	averageActiveLinesOnly: boolean;
	shrinkLegendForSmallScreens?: boolean;
	/** Y-axis tick formatter; omit for the plain one-decimal number (dollar charts pass a compact "$X.XK"). */
	tickRenderer?: (value: number) => string;
};

/**
 * One multi-facility comparison chart: a line per page-selected facility + the bold "Average"
 * anchor — shown whenever at least one facility line is drawn, visible by default. By default the
 * anchor spans ALL facilities in the set (so it stays consistent as the selection narrows); with
 * `averageActiveLinesOnly` it re-scopes to the drawn facilities (page-selected minus legend-hidden).
 * Which facilities render is decided by the page-level selector (`visibleDepartmentIds`); this card
 * only owns ad-hoc legend toggles (nothing seeded hidden; reset when the facility set or department
 * changes) and rescales the shared Y axis to the visible series only. Everything is purely
 * client-side — zero refetch.
 */
export const MultiFacilityChartCard: FC<Props> = ({
	title,
	dataTestId,
	series,
	facilityNames,
	isLoading,
	isError,
	sortedDepartmentIds,
	visibleDepartmentIds,
	currentDepartmentId,
	colors,
	clampRange,
	averageActiveLinesOnly,
	shrinkLegendForSmallScreens,
	tickRenderer,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { timezone } = useHospitalData();
	// The average line always reads just "Average" — its scope (whole network vs. selected
	// facilities) is governed by the average-scope toggle, not the label.
	const averageName = t("mc.multi.average.name");

	const displayNames = useMemo(
		() => (facilityNames ? dedupeFacilityDisplayNames(sortedDepartmentIds, facilityNames) : undefined),
		[facilityNames, sortedDepartmentIds],
	);

	// Ad-hoc legend toggles are seeded once per (facility set, chosen department, average label)
	// — NOT per selection change, so hiding facility A here survives adding/removing other
	// facilities via the page selector. Nothing starts hidden: facility visibility is
	// selector-owned and the average anchor is visible by default. The translated average label
	// is part of the key so a mid-session language switch re-seeds (a hidden anchor is stored by
	// its legend name, which just changed). The label is toggle-independent ("Average"), so
	// flipping the scope toggle never wipes the user's legend hides.
	const seedKey = `${sortedDepartmentIds.join(",")}|${currentDepartmentId}|${averageName}`;
	const seededKeyRef = useRef<string>(seedKey);
	const [hiddenSeries, setHiddenSeries] = useState<ReadonlySet<string>>(() => new Set());
	if (seededKeyRef.current !== seedKey) {
		seededKeyRef.current = seedKey;
		setHiddenSeries(new Set());
	}

	const onSeriesVisibilityToggle = useCallback(
		(seriesKey: string) => {
			// `visible` reports the state AFTER the toggle (hidden now ⇒ becoming visible).
			track("management_center_multi_series_toggled", { series: seriesKey, visible: hiddenSeries.has(seriesKey) });
			setHiddenSeries((prev) => {
				const next = new Set(prev);
				if (next.has(seriesKey)) next.delete(seriesKey);
				else next.add(seriesKey);
				return next;
			});
		},
		[track, hiddenSeries],
	);

	// Series are built from the page-selected subset only (deselected facility ⇒ no line, no
	// legend entry); the average anchor is appended inside the helper whenever a facility line is
	// drawn. By default the anchor is the mean of ALL facilities in the set; with
	// `averageActiveLinesOnly` it re-scopes to the drawn facilities (selected minus legend-hidden),
	// so legend toggles re-average it (hence `hiddenSeries` in the deps; the recompute is a cheap
	// client-side mean). Display names/colors above stay keyed off the FULL set, so the subset
	// reuses identical labels/hues.
	const groups = useMemo(() => {
		if (isLoading || !series || !displayNames) return undefined;
		// Default: the average spans ALL facilities in the set, so it stays consistent as the user
		// narrows the selection. When "active lines only" is on, it re-scopes to the drawn facilities
		// (selection minus legend-hidden) like the per-selection mean.
		const averageDepartmentIds = averageActiveLinesOnly ? visibleDepartmentIds.filter((id) => !hiddenSeries.has(displayNames[id])) : sortedDepartmentIds;
		return multiStatsToLineGroups({
			series,
			sortedDepartmentIds: visibleDepartmentIds,
			averageDepartmentIds,
			displayNames,
			colors,
			averageName,
			timezone,
			clampRange,
		});
	}, [
		isLoading,
		series,
		displayNames,
		sortedDepartmentIds,
		visibleDepartmentIds,
		averageActiveLinesOnly,
		hiddenSeries,
		colors,
		averageName,
		timezone,
		clampRange,
	]);
	const yDomain = useMemo(() => (groups ? visibleYDomain(groups, hiddenSeries) : undefined), [groups, hiddenSeries]);

	// Selection-aware: when every SELECTED facility is empty in range the card reads "No Data"
	// (the average anchor is derived from those facilities, so it can't have data either).
	const isEmpty = !isLoading && !isError && series !== undefined && !hasAnySeriesData(series, visibleDepartmentIds);

	return (
		<CommonDashboardBox
			data-testid={dataTestId}
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				gap: 1,
				width: { xs: "100%", lg: "calc(33% - 4px)" },
			}}
		>
			<CommonDashboardHeader label={title} />
			{isError || isEmpty ? (
				<MultiFacilityChartStateBox isError={isError} height={MULTI_CHART_HEIGHTS} />
			) : (
				<InHouseLineChart
					width={"100%"}
					height={MULTI_CHART_HEIGHTS}
					legendMaxHeight={LEGEND_MAX_HEIGHT}
					shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
					leftAxisData={
						groups
							? {
									dataGroups: groups,
									minValue: yDomain?.min,
									maxValue: yDomain?.max,
									tickRenderer: tickRenderer ?? plainAxisTick,
								}
							: undefined
					}
					hiddenSeries={hiddenSeries}
					onSeriesVisibilityToggle={onSeriesVisibilityToggle}
				/>
			)}
		</CommonDashboardBox>
	);
};
